import React, { useState, useEffect } from "react";
import { XMarkIcon, PrinterIcon, CalendarIcon } from "@heroicons/react/24/outline";
import AsyncSelect from "react-select/async";
import { privateAxios } from "../utils/axios";

const SalesReportModal = ({
  filters,
  setFilters,
  reportData,
  isGenerating,
  onGenerate,
}) => {
  const [dateSelectionMode, setDateSelectionMode] = useState("custom"); // "today", "single", "custom"
  const [singleDate, setSingleDate] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  // Fetch products on component mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await privateAxios.get("/products/all");
        if (response.data.success) {
          setProducts(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchProducts();
  }, []);

  const handleFilterChange = (field, value) => {
    console.log(`Setting filter ${field} to:`, value);
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Quick date selection handlers
  const handleTodaySelection = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters((prev) => ({
      ...prev,
      startDate: today,
      endDate: today,
    }));
    setDateSelectionMode("today");
  };

  const handleLast7DaysSelection = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    setFilters((prev) => ({
      ...prev,
      startDate: sevenDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
    }));
    setDateSelectionMode("last7days");
  };

  const handleLast30DaysSelection = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setFilters((prev) => ({
      ...prev,
      startDate: thirtyDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
    }));
    setDateSelectionMode("last30days");
  };

  const handleThisMonthSelection = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    
    setFilters((prev) => ({
      ...prev,
      startDate: firstDayOfMonth.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
    }));
    setDateSelectionMode("thismonth");
  };

  const handleSingleDateSelection = (date) => {
    setSingleDate(date);
    setFilters((prev) => ({
      ...prev,
      startDate: date,
      endDate: date,
    }));
    setDateSelectionMode("single");
  };

  const handleCustomRangeSelection = () => {
    setDateSelectionMode("custom");
  };

  const loadBranchOptions = async (inputValue, callback) => {
    if (!inputValue || inputValue.trim().length < 1) {
      callback([]);
      return;
    }
    try {
      const res = await privateAxios.get("/branches", {
        params: { q: inputValue, limit: 20 },
      });
      const branches = res.data?.data?.branches || [];
      callback(
        branches.map((b) => ({
          value: b.name,
          label: `${b.name} - ${b.receiverName}-${b.receiverNumber}`,
          branch: b,
        })),
      );
    } catch (err) {
      console.error("Error searching branches:", err);
      callback([]);
    }
  };

  const handleBranchSelect = (option) => {
    setSelectedBranch(option);
    handleFilterChange("branchName", option ? option.branch.name : "");
  };

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Get date display text
  const getDateDisplayText = () => {
    if (dateSelectionMode === "today") {
      return "Today";
    } else if (dateSelectionMode === "last7days") {
      return "Last 7 Days";
    } else if (dateSelectionMode === "last30days") {
      return "Last 30 Days";
    } else if (dateSelectionMode === "thismonth") {
      return "This Month";
    } else if (dateSelectionMode === "single" && singleDate) {
      return new Date(singleDate).toLocaleDateString();
    } else if (filters.startDate && filters.endDate) {
      if (filters.startDate === filters.endDate) {
        return new Date(filters.startDate).toLocaleDateString();
      } else {
        return `${new Date(filters.startDate).toLocaleDateString()} - ${new Date(filters.endDate).toLocaleDateString()}`;
      }
    }
    return "Select Date Range";
  };

  const handlePrintReport = () => {
    if (!reportData) return;

    const printWindow = window.open("", "_blank");
    const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Expense Report</title>
        <style>
          * { box-sizing: border-box; }
          body { font-family: Arial, sans-serif; margin: 0; padding: 15mm; font-size: 11px; color: #000; }
          .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 20px; }
          .header h1 { margin: 0; font-size: 20px; text-transform: uppercase; }
          .header h2 { margin: 5px 0 0; font-size: 14px; font-weight: normal; }

          .info-section { display: flex; justify-content: space-between; margin-bottom: 20px; }
          .info-block { width: 48%; }
          .info-block h3 { margin-bottom: 8px; font-size: 13px; border-bottom: 1px solid #000; padding-bottom: 4px; }
          .info-block p { margin: 2px 0; }

          table { width: 100%; max-width: 100%; border-collapse: collapse; margin-bottom: 30px; table-layout: fixed; }
          th, td { border: 1px solid #000; padding: 5px; text-align: left; vertical-align: top; word-wrap: break-word; overflow-wrap: break-word; }
          th { background: #f9f9f9; }
          .text-right { text-align: right; }
          .font-bold { font-weight: bold; }

          col.col-ref { width: 9%; }
          col.col-date { width: 8%; }
          col.col-branch { width: 12%; }
          col.col-receiver { width: 12%; }
          col.col-products { width: 39%; }
          col.col-subtotal { width: 10%; }
          col.col-total { width: 10%; }

          .products-table { margin: 0; width: 100%; table-layout: fixed; }
          .products-table col.p-name { width: 45%; }
          .products-table col.p-sku { width: 40%; }
          .products-table col.p-qty { width: 15%; }
          .products-table th, .products-table td { padding: 3px; font-size: 10px; word-wrap: break-word; overflow-wrap: break-word; border-color: #ccc; }
          .products-table tr:not(:last-child) td { border-bottom: 1px solid #ccc; }

          .summary { width: 260px; margin-left: auto; border: 1px solid #000; padding: 10px; }
          .summary h3 { text-align: center; margin: 0 0 10px; font-size: 13px; }
          .summary-row { display: flex; justify-content: space-between; padding: 4px 0; border-top: 1px solid #000; }

          .footer { margin-top: 50px; display: flex; justify-content: space-between; }
          .signature-block { width: 200px; text-align: center; }
          .signature-line { border-top: 1px solid #000; margin-top: 50px; }

          @media print {
            body { margin: 0; }
            .no-print { display: none; }
            table > tbody > tr { page-break-inside: avoid; break-inside: avoid; }
            thead { display: table-header-group; }
            tfoot { display: table-footer-group; }
          }
          @page { size: A4 landscape; margin: 12mm; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>JANANI GROUP</h1>
          <h2>IT Parts Inventory</h2>
          <h2>Expense Report</h2>
          <p>Generated on: ${new Date().toLocaleDateString()}</p>
        </div>
    
        <div class="info-section">
          <div class="info-block">
            <h3>Report Filters</h3>
            <p><strong>Branch:</strong> ${reportData.summary.filters.branchName || "All Branches"}</p>
            <p><strong>Receiver Phone:</strong> ${reportData.summary.filters.receiverPhone || "All Receiver Phones"}</p>
            <p><strong>Product:</strong> ${
              reportData.summary.filters.productId
                ? products.find(p => p._id === reportData.summary.filters.productId)?.name || "Unknown Product"
                : "All Products"
            }</p>
          </div>
          <div class="info-block">
            <h3>Report Period</h3>
            <p><strong>Start Date:</strong> ${reportData.summary.filters.startDate || "No start date"}</p>
            <p><strong>End Date:</strong> ${reportData.summary.filters.endDate || "No end date"}</p>
          </div>
        </div>
    
        <table>
          <colgroup>
            <col class="col-ref">
            <col class="col-date">
            <col class="col-branch">
            <col class="col-receiver">
            <col class="col-products">
            <col class="col-subtotal">
            <col class="col-total">
          </colgroup>
          <thead>
            <tr>
              <th>Reference</th>
              <th>Expense Date</th>
              <th>Branch</th>
              <th>Receiver</th>
              <th>Products</th>
              <th class="text-right">Sub Total</th>
              <th class="text-right">Total Amount</th>
            </tr>
          </thead>
          <tbody>
            ${reportData.expenses.map(expense => `
              <tr>
                <td>${expense.ref || "-"}</td>
                <td>${new Date(expense.expenseDate).toLocaleDateString()}</td>
                <td>${expense.branchName || "-"}<br><small>${expense.branchNumber || ""}</small></td>
                <td>${expense.receiver.name}<br><small>${expense.receiver.phone}</small></td>

                <td>
                  <table class="products-table">
                    <colgroup>
                      <col class="p-name">
                      <col class="p-sku">
                      <col class="p-qty">
                    </colgroup>
                    <thead>
                      <tr><th>Product</th><th>SKU</th><th>Qty</th></tr>
                    </thead>
                    <tbody>
                      ${expense.products.map(product => `
                        <tr>
                          <td>${product.name}</td>
                          <td>${(product.skus || []).join(", ")}</td>
                          <td>${product.quantity}</td>
                        </tr>
                      `).join("")}
                    </tbody>
                  </table>
                </td>
                <td class="text-right">৳${expense.subTotal}</td>
                <td class="text-right font-bold">৳${expense.totalAmount}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>
    
        <div class="summary">
          <h3>Report Summary</h3>
          <div class="summary-row">
            <span>Total Records:</span>
            <span class="font-bold">${reportData.summary.totalRecords}</span>
          </div>
          <div class="summary-row">
            <span>Total Amount:</span>
            <span class="font-bold">৳${reportData.summary.totalPrice}</span>
          </div>
        </div>
    
        <div class="footer">
          <div class="signature-block">
            <div class="signature-line"></div>
            <p>Prepared By</p>
          </div>
          <div class="signature-block">
            <div class="signature-line"></div>
            <p>Approved By</p>
          </div>
        </div>
      </body>
    </html>
    `;
    

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <div >
        {/* Filter Section */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h3 className="font-semibold mb-4">Filter Options</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Branch Name
              </label>
              <AsyncSelect
                cacheOptions
                defaultOptions={false}
                value={selectedBranch}
                loadOptions={loadBranchOptions}
                onChange={handleBranchSelect}
                isClearable
                placeholder="Search by branch name..."
                noOptionsMessage={({ inputValue }) =>
                  inputValue ? "No branches found" : "Type to search branches"
                }
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Receiver Phone
              </label>
              <input
                type="text"
                value={filters.receiverPhone}
                onChange={(e) =>
                  handleFilterChange("receiverPhone", e.target.value)
                }
                placeholder="Enter receiver phone"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product (Optional)
              </label>
              <select
                value={filters.productId || ""}
                onChange={(e) =>
                  handleFilterChange("productId", e.target.value)
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Products</option>
                {products.map((product) => (
                  <option key={product._id} value={product._id}>
                    {product.name} - {product.category}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Enhanced Date Selection */}
          <div className="border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Range
            </label>
            
            {/* Quick Selection Buttons */}
            <div className="flex flex-wrap gap-2 mb-3">
              <button
                onClick={handleTodaySelection}
                className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                  dateSelectionMode === "today"
                    ? "bg-blue-500 text-white border-blue-500"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Today
              </button>
              <button
                onClick={handleLast7DaysSelection}
                className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                  dateSelectionMode === "last7days"
                    ? "bg-blue-500 text-white border-blue-500"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Last 7 Days
              </button>
              <button
                onClick={handleLast30DaysSelection}
                className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                  dateSelectionMode === "last30days"
                    ? "bg-blue-500 text-white border-blue-500"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Last 30 Days
              </button>
              <button
                onClick={handleThisMonthSelection}
                className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                  dateSelectionMode === "thismonth"
                    ? "bg-blue-500 text-white border-blue-500"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                This Month
              </button>
              <button
                onClick={() => setDateSelectionMode("single")}
                className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                  dateSelectionMode === "single"
                    ? "bg-blue-500 text-white border-blue-500"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Single Date
              </button>
              <button
                onClick={handleCustomRangeSelection}
                className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                  dateSelectionMode === "custom"
                    ? "bg-blue-500 text-white border-blue-500"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Custom Range
              </button>
            </div>

            {/* Date Display */}
            <div className="flex items-center gap-2 mb-3 p-2 bg-white border border-gray-300 rounded-md">
              <CalendarIcon className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-700">
                {getDateDisplayText()}
              </span>
            </div>

            {/* Date Input Fields */}
            {dateSelectionMode === "single" && (
              <div>
                <input
                  type="date"
                  value={singleDate}
                  onChange={(e) => handleSingleDateSelection(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            {dateSelectionMode === "custom" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) =>
                      handleFilterChange("startDate", e.target.value)
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange("endDate", e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={() => {
                console.log("Current filters being sent:", filters);
                onGenerate();
              }}
              disabled={isGenerating}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? "Generating..." : "Generate Report"}
            </button>
            {reportData && (
              <button
                onClick={handlePrintReport}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md flex items-center gap-2"
              >
                <PrinterIcon className="h-4 w-4" />
                Print Report
              </button>
            )}
          </div>
        </div>

        {/* Report Results */}
        {reportData && (
          <div>
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-900">
                      Reference
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-900">
                      Expense Date
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-900">
                      Receiver
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-900">
                      Products
                    </th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-900">
                      Sub Total
                    </th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-900">
                      Total Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {reportData.expenses.map((expense) => (
                    <tr key={expense.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-900">{expense.ref}</td>
                      <td className="px-4 py-3 text-gray-700">
                        {new Date(expense.expenseDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <div className="font-medium">
                          {expense.receiver.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {expense.receiver.phone}
                        </div>
                        <div className="text-xs text-gray-500">
                          {expense.receiver.address}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <div className="max-w-xs">
                          {expense.products.map((product, index) => (
                            <div
                              key={index}
                              className="text-xs border-b border-gray-100 py-1"
                            >
                              <div className="font-medium">{product.name}
                                <div>
                                SKUS: {product?.skus?.map((sku, index) => (
                              <span
                                key={index}
                                className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded"
                              >
                                {sku}
                              </span>
                            ))}
                            </div>
                              </div>
                              <div className="text-gray-500">
                              Qty: {product.quantity}
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">
                        ৳{expense.subTotal}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">
                        ৳{expense.totalAmount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end">
              <div className="w-80 bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3 text-center">
                  Report Summary
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between py-2">
                    <span className="text-sm text-gray-600">
                      Total Records:
                    </span>
                    <span className="font-semibold">
                      {reportData.summary.totalRecords}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-t border-blue-200">
                    <span className="text-sm text-gray-600">Total Amount:</span>
                    <span className="font-bold text-lg text-blue-600">
                      ৳{reportData.summary.totalPrice}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
     
    </div>
  );
};

export default SalesReportModal;
